import React from 'react';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';
import { Box, Flex } from 'rebass/styled-components';

export const HiresMainSkeleton = () => {
  const { colors } = useTheme();

  return (
    <Flex flex="1" sx={{ position: 'relative', bg: 'neutral.0', overflow: 'hidden', flexDirection: 'column' }}>
      {/* Toolbar Skeleton */}
      <Flex
        sx={{
          borderBottom: '1px solid',
          borderColor: 'border.subtle',
          pb: '8px',
          pr: '16px',
          pl: '16px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left-aligned buttons */}
        <Flex sx={{ gap: '8px' }}>
          <Box sx={{ width: '80px', height: '24px' }}>
            <ContentLoader
              speed={2}
              width={80}
              height={24}
              viewBox="0 0 80 24"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              <rect x="0" y="0" rx="4" ry="4" width="80" height="24" />
            </ContentLoader>
          </Box>
          <Box sx={{ width: '80px', height: '24px' }}>
            <ContentLoader
              speed={2}
              width={80}
              height={24}
              viewBox="0 0 80 24"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              <rect x="0" y="0" rx="4" ry="4" width="80" height="24" />
            </ContentLoader>
          </Box>
          <Box sx={{ width: '80px', height: '24px' }}>
            <ContentLoader
              speed={2}
              width={80}
              height={24}
              viewBox="0 0 80 24"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              <rect x="0" y="0" rx="4" ry="4" width="80" height="24" />
            </ContentLoader>
          </Box>
          <Box sx={{ width: '80px', height: '24px' }}>
            <ContentLoader
              speed={2}
              width={80}
              height={24}
              viewBox="0 0 80 24"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              <rect x="0" y="0" rx="4" ry="4" width="80" height="24" />
            </ContentLoader>
          </Box>
        </Flex>
        {/* Right-aligned export button */}
        <Box sx={{ width: '100px', display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ width: '80px', height: '24px' }}>
            <ContentLoader
              speed={2}
              width={80}
              height={24}
              viewBox="0 0 80 24"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              <rect x="0" y="0" rx="4" ry="4" width="80" height="24" />
            </ContentLoader>
          </Box>
        </Box>
      </Flex>

      <Flex flex="1" sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Filter Sidebar Skeleton */}
        <Box
          sx={{
            bg: 'neutral.0',
            width: '264px',
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'border.subtle',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Filters Title */}
          <Box sx={{ p: '8px 16px 16px 16px' }}>
            <ContentLoader
              speed={2}
              width={232}
              height={24}
              viewBox="0 0 232 24"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              <rect x="0" y="0" rx="4" ry="4" width="80" height="24" />
            </ContentLoader>
          </Box>
          {/* Filter items */}
          <Box sx={{ px: '16px' }}>
            <ContentLoader
              speed={2}
              width={232}
              height="100%"
              viewBox="0 0 232 600"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              <rect x="0" y="0" rx="4" ry="4" width="232" height="40" />
              <rect x="0" y="56" rx="4" ry="4" width="232" height="40" />
              <rect x="0" y="112" rx="4" ry="4" width="232" height="40" />
              <rect x="0" y="168" rx="4" ry="4" width="232" height="40" />
              <rect x="0" y="224" rx="4" ry="4" width="232" height="40" />
            </ContentLoader>
          </Box>
        </Box>

        {/* Table Skeleton */}
        <Box flex="1" sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Table Header */}
          <Box
            sx={{
              borderBottom: '1px solid',
              borderColor: 'border.subtle',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ContentLoader
              speed={2}
              width="100%"
              height={32}
              viewBox="0 0 1400 32"
              backgroundColor={colors.neutral[50]}
              foregroundColor={colors.neutral[20]}
            >
              {/* Column headers matching actual widths: Contact (200), Company (200), Job title (240), Assisted by team (160), Hired date (160), Source (200), Contact of (flex) */}
              <rect x="16" y="8" rx="4" ry="4" width="80" height="16" />
              <rect x="216" y="8" rx="4" ry="4" width="80" height="16" />
              <rect x="416" y="8" rx="4" ry="4" width="100" height="16" />
              <rect x="656" y="8" rx="4" ry="4" width="120" height="16" />
              <rect x="816" y="8" rx="4" ry="4" width="80" height="16" />
              <rect x="1016" y="8" rx="4" ry="4" width="80" height="16" />
              <rect x="1216" y="8" rx="4" ry="4" width="100" height="16" />
            </ContentLoader>
          </Box>

          {/* Table Rows */}
          <Box flex="1" sx={{ overflow: 'hidden' }}>
            {Array.from({ length: 10 }, (_, index) => `skeleton-row-${index}`).map((rowId) => (
              <Box
                key={rowId}
                sx={{
                  height: '48px',
                  borderBottom: '1px solid',
                  borderColor: 'border.subtle',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ContentLoader
                  speed={2}
                  width="100%"
                  height={48}
                  viewBox="0 0 1400 48"
                  backgroundColor={colors.neutral[50]}
                  foregroundColor={colors.neutral[20]}
                >
                  {/* Contact column (with avatar) - width 200, padding left 16px */}
                  <circle cx="32" cy="24" r="12" />
                  <rect x="52" y="16" rx="4" ry="4" width="120" height="16" />
                  {/* Company - width 200 */}
                  <rect x="216" y="16" rx="4" ry="4" width="140" height="16" />
                  {/* Job title - width 240 */}
                  <rect x="416" y="16" rx="4" ry="4" width="180" height="16" />
                  {/* Assisted by team - width 160 */}
                  <rect x="656" y="16" rx="4" ry="4" width="100" height="16" />
                  {/* Hired date - width 160 */}
                  <rect x="816" y="16" rx="4" ry="4" width="100" height="16" />
                  {/* Source - width 200 */}
                  <rect x="1016" y="16" rx="4" ry="4" width="140" height="16" />
                  {/* Contact of - flex column */}
                  <rect x="1216" y="16" rx="4" ry="4" width="150" height="16" />
                </ContentLoader>
              </Box>
            ))}
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};
